import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, LockKeyhole, RefreshCw, ShieldCheck } from "lucide-react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { getHodOverview } from "../../lib/performance-lens/review-api.ts";
import type { HodOverview } from "../../lib/performance-lens/types.ts";
import { usePerformanceLensAccess } from "../../lib/performance-lens/use-access.ts";
import ScreenSkeleton from "../../components/ui/screen-skeleton/screen-skeleton.tsx";
import styles from "./hod-overview-page.module.css";
import { Select } from "../../components/ui/select/select.tsx";

export default function HodOverviewPage() {
  const { access, loading: accessLoading, error: accessError, refresh: refreshAccess } = usePerformanceLensAccess();
  const [data, setData] = useState<HodOverview | null>(null);
  const [projectId, setProjectId] = useState<number | "all">("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      const result = await getHodOverview(signal);
      if (!signal?.aborted) setData(result);
    } catch (cause) {
      if (!signal?.aborted) setError(cause instanceof Error ? cause.message : "Could not load Head of Delivery overview.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!access?.hodProjects?.length) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [access?.hodProjects?.length, load]);

  const cycles = useMemo(() => data?.cycles.filter(row => projectId === "all" || row.projectId === projectId) ?? [], [data, projectId]);
  const assigned = cycles.reduce((sum, row) => sum + row.assignedReviews, 0);
  const submitted = cycles.reduce((sum, row) => sum + row.submittedReviews, 0);

  return <PageShell><main className={styles.page}>
    {accessLoading && <ScreenSkeleton label="Checking Head of Delivery access" variant="dashboard" />}
    {accessError && <div className={styles.error} role="alert"><AlertCircle size={18} />Could not verify access.<button type="button" onClick={() => void refreshAccess()}>Retry</button></div>}
    {!accessLoading && !accessError && !access?.hodProjects?.length && <div className={styles.notice}><LockKeyhole size={22} /><div><strong>Head of Delivery access required</strong><p>This view is available only for projects where you are named as Head of Delivery.</p></div></div>}
    {!accessLoading && !!access?.hodProjects?.length && <>
      <section className={styles.intro}><ShieldCheck size={22} /><div><h2>Delivery overview</h2><p>Submission progress for your projects. Peer ratings appear only as published aggregates from at least three reviewers; individual answers and reviewer names are never shown here.</p></div></section>
      {error && <div className={styles.error} role="alert"><AlertCircle size={18} />{error}<button type="button" onClick={() => void load()}>Retry</button></div>}
      <section className={styles.toolbar} aria-label="Overview filters">
        <Select id="lens-hod-project" label="Project" labelLayout="inline" value={String(projectId)}
          options={[{ value: "all", label: "All My Projects" }, ...(data?.projects ?? access.hodProjects).map(project => ({ value: String(project.id), label: project.name }))]}
          onChange={value => setProjectId(value === "all" ? "all" : Number(value))} />
        <button type="button" disabled={loading} onClick={() => void load()}><RefreshCw size={16} />Refresh</button>
      </section>
      {loading && !data && <ScreenSkeleton label="Loading delivery overview" variant="dashboard" />}
      {data && <>
        <div className={styles.stats} aria-label="Submission summary">
          <div className={styles.card}><span>Assigned reviews</span><strong>{assigned}</strong></div>
          <div className={styles.card}><span>Submitted</span><strong>{submitted}</strong></div>
          <div className={styles.card}><span>Awaiting submission</span><strong>{assigned - submitted}</strong></div>
        </div>
        <section className={styles.card} aria-labelledby="lens-hod-cycles-title">
          <div className={styles.sectionHeading}><div><h2 id="lens-hod-cycles-title">Project feedback by cycle</h2><p>Counts include assigned peer and manager reviews. Peer scores are hidden until publication and the three-reviewer threshold.</p></div><span>{cycles.length} cycle{cycles.length === 1 ? "" : "s"}</span></div>
          {cycles.length ? <div className={styles.tableScroll}><table><thead><tr><th scope="col">Project</th><th scope="col">Cycle</th><th scope="col">Submitted</th><th scope="col">Awaiting</th><th scope="col">Late</th><th scope="col">Published peer average</th></tr></thead><tbody>
            {cycles.map(row => <tr key={`${row.projectId}:${row.cycleNumber}`}><th scope="row">{row.projectName}</th><td>{row.cycleNumber} <small>{row.cycleStatus}</small></td><td>{row.submittedReviews} of {row.assignedReviews}</td><td>{row.assignedReviews - row.submittedReviews}</td><td>{row.lateReviews}</td><td>{row.publishedPeerAverage === null ? <span className={styles.muted}>Hidden</span> : `${Number(row.publishedPeerAverage).toFixed(2)} / 5`}</td></tr>)}
          </tbody></table></div> : <div className={styles.empty}><CheckCircle2 size={24} /><strong>No review assignments yet</strong><p>Once an administrator builds a review queue, submission progress will appear here.</p></div>}
        </section>
      </>}
    </>}
  </main></PageShell>;
}
